import { CatchErrors, MenuModule, routing, SystemModule, t, triggerWindowResize, useCurrentPage, useIntervalAjaxRequest, useIntervalAjaxRequests, useMapDispatch, useMapState } from "@comact/crc";
import { LoadingSpinner, Page, PageForbidden, PageNotFound } from "@comact/crc/modules/kit";
import _ from "lodash";
import React from "react";
import { useParams } from "react-router-dom";
import { extractLocationFromNodeId, extractMillFromNodeId, extractOrganizationFromNodeId, NodesModule } from "../";
import { FeatureFlagsModule } from "../../featureFlags";
import { UserModule } from "../../users";
import { PageNodeContext, useGetOrFetchNode } from "../hooks";
import { IPerspective } from "../perspectives/model";
import * as perspectivesSelectors from "../perspectives/selectors";
import { getPerspectiveAccessState } from "../perspectives/selectors";
import { HeaderBreadcrumb } from "./HeaderBreadcrumb";

export { usePageNodeContext } from "../hooks";

const PageLogin = React.lazy(() => import("../../users/components/PageLogin"));

// eslint-disable-next-line complexity
export const PageNode = React.memo<{ perspective: IPerspective; children: React.ReactNode; }>(({ perspective, children }) => {
    const { nodeId: paramNodeId } = useParams<{ nodeId: string; }>();

    const nodeId = useMapState((state) => (
        paramNodeId ||
        _.map(
            NodesModule.selectors.getNodesByTemplateName(state, "customer") || {},
            ({ id }) => id
        )?.[0] // if no nodeId is passed as param, use the customer node
    ), [paramNodeId]);

    const { fetchAllNodes, fetchTopNodes } = useMapDispatch((dispatch) => NodesModule.getRequests(dispatch), [nodeId]);
    const { getAliases } = useMapDispatch((dispatch) => NodesModule.getRequests(dispatch), []);
    const isLoggedIn = useMapState((state) => UserModule.selectors.isLoggedIn(state));
    useIntervalAjaxRequests({
        createAjaxRequests: [
            fetchAllNodes,
            fetchTopNodes,
            getAliases,
        ],
        timeout: 10 * 1000,
    });

    const { hasTwin } = useMapState((state) => SystemModule.selectors.getAllSystem(state));

    const millNodeId = extractMillFromNodeId(nodeId);
    const locationNodeId = extractLocationFromNodeId(nodeId);
    const orgNodeId = extractOrganizationFromNodeId(nodeId);

    const node = useGetOrFetchNode(nodeId);

    const nodesValue = useMapState((state) => NodesModule.selectors.getAllBasicNodes(state));
    const nodesReady = nodesValue !== null && nodesValue !== undefined;
    const nodesEmpty = nodesReady && _.size(nodesValue) == 0;

    const allowNodeLessAccess = React.useMemo(() => !!perspective?.startsWith("organization-settings"), [perspective]);

    const menuActions = useMapDispatch((dispatch) => MenuModule.getActions(dispatch));

    const [overridePageName, _setPageName] = React.useState<string>(null);

    const isMountRef = React.useRef<boolean>(false);
    React.useEffect(() => {
        isMountRef.current = true;
        return () => { isMountRef.current = false; };
    }, []);

    const nodeActions = useMapDispatch((dispatch) => NodesModule.getActions(dispatch));

    const [millNode, locationNode, nodeMismatch, millMismatch, locationMismatch, organizationMismatch] = useMapState((state) => [
        NodesModule.selectors.getDetailedNodeById(state, millNodeId),
        NodesModule.selectors.getDetailedNodeById(state, locationNodeId),
        nodeId != NodesModule.selectors.getCurrentNodeId(state),
        millNodeId != NodesModule.selectors.getCurrentMillNodeId(state),
        locationNodeId != NodesModule.selectors.getCurrentLocationNodeId(state),
        orgNodeId != NodesModule.selectors.getCurrentOrganizationNodeId(state),
    ], [nodeId, millNodeId, locationNodeId, orgNodeId]);

    // Set/unset currentNodeId
    React.useEffect(() => {
        if (nodeMismatch) { // node has change in redux state
            nodeActions.setCurrentNodeId(nodeId || null);
        }
        const allowSyncWithoutNodes = (allowNodeLessAccess || nodesReady);
        if (allowSyncWithoutNodes) { // be sure at least one node has been fetch from the server or the next codes won't work on first page load
            if (millMismatch) { // mill node has change in redux state
                nodeActions.setCurrentMillNodeId(millNodeId);
            }
            if (locationMismatch) {
                nodeActions.setCurrentLocationNodeId(locationNodeId);
            }
            if (organizationMismatch) {
                nodeActions.setCurrentOrganizationNodeId(orgNodeId);
            }
        }
        // eslint-disable-next-line @stylistic/max-len
    }, [nodeActions, nodeId, nodesReady, millNodeId, millMismatch, nodeMismatch, locationMismatch, locationNodeId, organizationMismatch, allowNodeLessAccess, millNode, locationNode, orgNodeId]);

    useIntervalAjaxRequest((dispatch) => orgNodeId && (
        FeatureFlagsModule.getRequests(dispatch).getFeatureFlags(orgNodeId)
    ), 60 * 1000, [orgNodeId]);

    const hasMismatch = allowNodeLessAccess ? false : (nodeMismatch || millMismatch);

    // Create perspective menu
    const getNodePerpectivesMenu = React.useMemo(() => perspectivesSelectors.makeGetNodePerpectivesMenuAndHeader(), []);
    const perspectivesMenu = useMapState((state) => getNodePerpectivesMenu({ state, nodeId }), [nodeId]);
    React.useEffect(() => {
        if (!perspective) return undefined;

        menuActions.updateSome(perspectivesMenu);
        const trig = setTimeout(() => triggerWindowResize(), 1); // we need to trigger a resize after changing the menu
        return () => {
            clearTimeout(trig);
            menuActions.removeByCategory("perspective"); // remove menu when unmounting
        };
    }, [menuActions, nodeId, perspective, getNodePerpectivesMenu, perspectivesMenu, nodesReady]);

    const perspectivePermissionState = useMapState((state) => (
        getPerspectiveAccessState(state, perspective, node?.templateName, locationNodeId)
    ), [perspective, node?.templateName, locationNodeId]);

    const setPageName = React.useCallback((name: string) => {
        // We can't use React.useState (setState()) synchronously because it cause some console bugs..
        // The set timeout is here to prevent the following error : "Warning: Cannot update a component while rendering a different component". To locate the bad setState() call...
        setTimeout(() => {
            if (isMountRef.current) _setPageName(name);
        }, 0);
    }, []);

    const pageName = perspectivePermissionState == "allowed"
        ? overridePageName || t(`pages.menu.perspective.${perspective}`)
        : "";

    const displayNodeName = useMapState((state) => NodesModule.selectors.getNodeDisplayName(state, nodeId), [nodeId]);

    const pageWindowName = perspectivePermissionState == "allowed"
        ? pageName + " - " + (displayNodeName || t("node.root"))
        : "";

    useCurrentPage(pageName, pageWindowName);

    const isOrgSettings = !!perspective?.startsWith("organization-settings");
    if (isOrgSettings) {
        return (
            <PageNodeContext.Provider
                value={{
                    nodeId: null,
                    node: null,
                    locationNodeId: null,
                    millNodeId: null,
                    millNode: null,
                    perspective,
                    setPageName,
                }}
            >
                {(process.env.EXEC_MODE == "icp" || perspective == "details") && <HeaderBreadcrumb />}
                <React.Fragment key="org-settings-nodeless">
                    <CatchErrors>{children}</CatchErrors>
                </React.Fragment>
            </PageNodeContext.Provider>
        );
    }

    if (!routing.currentPath.startsWith("/login")) { // skip some tests if on login page
        if (hasTwin && !nodesReady && !allowNodeLessAccess) return <LoadingSpinner.Absolute />;
        if (hasTwin && nodesReady && nodesEmpty && !allowNodeLessAccess) {
            return (
                <Page kContainerSize="800px">
                    <HeaderBreadcrumb />
                    <div style={{ padding: 24 }}>
                        <h3 style={{ margin: 0 }}>{t("node.timeoutLabel")}</h3>
                        <p style={{ marginTop: 8, opacity: 0.8 }}>
                            {t("node.timeoutMessage")}
                        </p>
                    </div>
                </Page>
            );
        }
    }

    if (hasMismatch) return <LoadingSpinner.Absolute />;
    if (perspectivePermissionState == "notReady") return <LoadingSpinner.Absolute />;
    if (perspectivePermissionState == "notFound") return <PageNotFound />;
    if (perspectivePermissionState == "denied") return <PageForbidden />;

    if (!isLoggedIn) return <PageLogin useRedirect={false} />;

    return (
        <PageNodeContext.Provider value={{ nodeId, node, locationNodeId, millNode, millNodeId, perspective, setPageName }}>
            {(process.env.EXEC_MODE == "icp" || millNode?.id != node?.id || perspective == "details") && (
                <HeaderBreadcrumb />
            )}
            <React.Fragment key={nodeId}>{/* Be sure that if the same Page component is loaded twice it will rerender */}
                <CatchErrors>
                    {children}
                </CatchErrors>
            </React.Fragment>
        </PageNodeContext.Provider>
    );
});